/**
 * WebhooksPage — W2.5
 *
 * Route /developer/webhooks
 * Premium-Guard: HTTP 402 → PremiumRequired
 * Webhook-Liste + Create-Modal + Delete-Bestätigung + Test-Delivery
 * Light/Dark-Mode aware via CSS custom properties.
 */
import { useState } from 'react'
import { Plus, Loader2, Trash2, FlaskConical, History, ChevronDown, ChevronUp, X } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useWebhooks, EVENT_LABEL_KEYS, WEBHOOK_EVENTS, type WebhookConfig, type WebhookTestResult, type CreateWebhookParams } from './useWebhooks'
import { useApiUsage } from './useApiUsage'
import { TokenRevealBox } from './TokenRevealBox'
import { PremiumRequired } from '../../components/PremiumRequired'
import { apiErrorMessage, apiErrorStatus } from '../../lib/errors/apiErrorMessage'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'

// --- Hilfsfunktionen ---

function truncateUrl(url: string, max = 50): string {
  return url.length > max ? url.slice(0, max) + '...' : url
}

// --- Loading Skeleton ---

function SkeletonRow() {
  return (
    <div className="list-row skeleton" aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
      <div style={{ flex: 1 }}>
        <div style={{ width: 220, height: 14, background: 'color-mix(in srgb, var(--text) 10%, transparent)', borderRadius: 4, marginBottom: 6, animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }} />
        <div style={{ width: 140, height: 12, background: 'color-mix(in srgb, var(--text) 8%, transparent)', borderRadius: 4, animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }} />
      </div>
      <div style={{ width: 44, height: 44, background: 'color-mix(in srgb, var(--text) 6%, transparent)', borderRadius: 8, animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }} />
      <div style={{ width: 44, height: 44, background: 'color-mix(in srgb, var(--text) 6%, transparent)', borderRadius: 8, animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }} />
    </div>
  )
}

function LoadingSkeleton() {
  const { t } = useTranslation('developer')
  return (
    <div aria-busy="true" aria-label={t('webhooks.loading')} data-testid="loading-skeleton">
      {Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)}
    </div>
  )
}

// --- Error State ---

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('developer')
  return (
    <div className="error-state" role="alert" data-testid="error-state">
      <p>{t('webhooks.error')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Empty State ---

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  const { t } = useTranslation('developer')
  return (
    <div className="empty-state" data-testid="empty-state" style={{ padding: 32, textAlign: 'center' }}>
      <p style={{ marginBottom: 16, color: 'var(--text-subtle)' }}>
        {t('webhooks.empty')}
      </p>
      <button className="btn btn-primary" onClick={onCreateClick} data-testid="btn-create-empty">
        {t('webhooks.emptyCreate')}
      </button>
    </div>
  )
}

// --- Event-Filter-Chips ---

type EventChipsProps = { events: string[] }

function EventChips({ events }: EventChipsProps) {
  const { t } = useTranslation('developer')

  if (events.length === 0) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 500,
          background: 'color-mix(in srgb, var(--text) 8%, transparent)',
          color: 'var(--text-subtle)',
        }}
      >
        {t('webhooks.eventFilterAll')}
      </span>
    )
  }

  const MAX_VISIBLE = 3
  const visible = events.slice(0, MAX_VISIBLE)
  const rest = events.length - MAX_VISIBLE

  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {visible.map(ev => (
        <span
          key={ev}
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 500,
            background: 'color-mix(in srgb, var(--primary, #6366f1) 12%, transparent)',
            color: 'var(--primary, #6366f1)',
            border: '1px solid color-mix(in srgb, var(--primary, #6366f1) 25%, transparent)',
          }}
        >
          {EVENT_LABEL_KEYS[ev] ? t(EVENT_LABEL_KEYS[ev]) : ev}
        </span>
      ))}
      {rest > 0 && (
        <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
          {t('webhooks.eventFilterMore', { count: rest })}
        </span>
      )}
    </span>
  )
}

// --- Webhook-Row ---

type WebhookRowProps = {
  webhook: WebhookConfig
  onDelete: (id: number) => void
  onTest: (id: number) => Promise<WebhookTestResult>
  isDeleting: boolean
  isTesting: boolean
}

function WebhookRow({ webhook, onDelete, onTest, isDeleting, isTesting }: WebhookRowProps) {
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null)
  const { t } = useTranslation('developer')

  // F5 (Welle-4b): Der Test-Aufruf wirft bei HTTP >= 400 bzw. Netzwerkfehler.
  // Ohne catch endete das als unhandled rejection — die Zeile blieb stumm und
  // der Spinner-Zustand wirkte wie ein Erfolg. Jetzt landet JEDER Fehler als
  // uebersetzte Ergebnisbox (Status + Meldung) in der Zeile.
  async function handleTest() {
    try {
      const result = await onTest(webhook.id)
      setTestResult(result)
    } catch (err) {
      const status = apiErrorStatus(err)
      setTestResult({
        success: false,
        http_status: status ?? null,
        response_body: null,
        duration_ms: null,
        error: apiErrorMessage(err, t('webhooks.row.testRequestFailed')),
      })
    }
  }

  return (
    <div
      className="list-row"
      data-testid={`webhook-row-${webhook.id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
        {/* URL + Badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span
              data-testid={`webhook-url-${webhook.id}`}
              style={{
                fontFamily: 'ui-monospace, Consolas, monospace',
                fontSize: 13,
                color: 'var(--text-h)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
              title={webhook.url}
            >
              {truncateUrl(webhook.url)}
            </span>

            {/* Aktiv/Inaktiv-Badge */}
            <span
              data-testid={`webhook-active-badge-${webhook.id}`}
              style={{
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 600,
                background: webhook.active
                  ? 'color-mix(in srgb, var(--success, #16a34a) 12%, transparent)'
                  : 'color-mix(in srgb, var(--text) 8%, transparent)',
                color: webhook.active ? 'var(--success, #16a34a)' : 'var(--text-subtle)',
                border: webhook.active
                  ? '1px solid color-mix(in srgb, var(--success, #16a34a) 25%, transparent)'
                  : '1px solid var(--border)',
                flexShrink: 0,
              }}
            >
              {webhook.active ? t('webhooks.badges.active') : t('webhooks.badges.inactive')}
            </span>

            {/* Fehler-Badge */}
            {webhook.consecutive_failures > 0 && (
              <span
                data-testid={`webhook-failures-badge-${webhook.id}`}
                style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  background: 'color-mix(in srgb, #f59e0b 12%, transparent)',
                  color: '#b45309',
                  border: '1px solid color-mix(in srgb, #f59e0b 30%, transparent)',
                  flexShrink: 0,
                }}
              >
                {t('webhooks.badges.failures', { count: webhook.consecutive_failures })}
              </span>
            )}
          </div>

          {/* Event-Filter-Chips */}
          <EventChips events={webhook.event_filter} />
        </div>

        {/* Aktions-Buttons */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {/* Delivery-Log-Link */}
          <Link
            to="/developer/webhooks/$webhookId/deliveries"
            params={{ webhookId: String(webhook.id) }}
            className="btn btn-icon btn-ghost"
            aria-label={t('webhooks.row.deliveriesLabel')}
            data-testid={`webhook-deliveries-link-${webhook.id}`}
            style={{ minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: 8 }}
          >
            <History size={16} aria-hidden="true" />
          </Link>

          {/* Test-Button */}
          <button
            className="btn btn-icon btn-ghost"
            aria-label={t('webhooks.row.testLabel')}
            data-testid={`webhook-test-btn-${webhook.id}`}
            onClick={() => { void handleTest() }}
            disabled={isTesting}
            style={{ minWidth: 44, minHeight: 44, border: '1px solid var(--border)', borderRadius: 8, color: 'var(--primary, #6366f1)' }}
          >
            {isTesting
              ? <Loader2 size={16} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
              : <FlaskConical size={16} aria-hidden="true" />
            }
          </button>

          {/* Löschen-Button */}
          <DemoGuardedButton
            className="btn btn-icon btn-ghost"
            aria-label={t('webhooks.row.deleteLabel')}
            data-testid={`webhook-delete-btn-${webhook.id}`}
            onClick={() => onDelete(webhook.id)}
            disabled={isDeleting}
            style={{ minWidth: 44, minHeight: 44, border: '1px solid var(--border)', borderRadius: 8, color: 'var(--danger, #dc2626)' }}
          >
            {isDeleting
              ? <Loader2 size={16} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
              : <Trash2 size={16} aria-hidden="true" />
            }
          </DemoGuardedButton>
        </div>
      </div>

      {/* Test-Ergebnis Inline-Box */}
      {testResult && (
        <div
          data-testid={`webhook-test-result-${webhook.id}`}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 13,
            background: testResult.success
              ? 'color-mix(in srgb, var(--success, #16a34a) 10%, transparent)'
              : 'color-mix(in srgb, var(--danger, #dc2626) 10%, transparent)',
            border: testResult.success
              ? '1px solid color-mix(in srgb, var(--success, #16a34a) 25%, transparent)'
              : '1px solid color-mix(in srgb, var(--danger, #dc2626) 25%, transparent)',
            color: testResult.success ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)',
          }}
          className={`webhook-test-result ${testResult.success ? 'is-success' : 'is-danger'}`}
          role="status"
          aria-live="polite"
        >
          {testResult.success
            ? t('webhooks.row.testSuccess', { status: testResult.http_status, duration: testResult.duration_ms })
            : testResult.http_status !== null
              ? t('webhooks.row.testFailedWithStatus', {
                  status: testResult.http_status,
                  error: testResult.error ?? t('webhooks.row.testRequestFailed'),
                })
              : t('webhooks.row.testFailed', {
                  error: testResult.error ?? t('webhooks.row.testRequestFailed'),
                })
          }
        </div>
      )}
    </div>
  )
}

// --- Delete-Confirm-Dialog ---

type DeleteConfirmProps = {
  webhookUrl: string
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
}

function DeleteConfirmDialog({ webhookUrl, onConfirm, onCancel, isDeleting }: DeleteConfirmProps) {
  const { t } = useTranslation('developer')
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('deleteWebhookDialog.ariaLabel')}
      data-testid="delete-confirm-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: 24,
          width: '100%',
          maxWidth: 440,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          boxSizing: 'border-box',
        }}
      >
        <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600 }}>{t('deleteWebhookDialog.title')}</h2>
        <p style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--text-subtle)' }}>
          {t('deleteWebhookDialog.description')}
        </p>
        <p style={{ margin: '0 0 24px', fontSize: 13, fontFamily: 'ui-monospace, Consolas, monospace', color: 'var(--text-h)', wordBreak: 'break-all' }}>
          {webhookUrl}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={isDeleting}
            data-testid="delete-cancel-btn"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting}
            data-testid="delete-confirm-btn"
            style={{ background: 'var(--danger, #dc2626)', color: '#fff', border: 'none' }}
          >
            {isDeleting ? t('deleteWebhookDialog.deleting') : t('common:actions.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Create-Webhook-Modal ---

type CreateWebhookModalProps = {
  isOpen: boolean
  onClose: () => void
  onCreateWebhook: (params: CreateWebhookParams) => Promise<string>
}

function CreateWebhookModalContent({
  onClose,
  onCreateWebhook,
}: Omit<CreateWebhookModalProps, 'isOpen'>) {
  const [step, setStep] = useState<'form' | 'reveal'>('form')
  const [createdSecret, setCreatedSecret] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation('developer')

  // Form-State
  const [url, setUrl] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [selectedEvents, setSelectedEvents] = useState<string[]>([])
  const [secret, setSecret] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  // HMAC-Hinweis aufklappbar
  const [hmacOpen, setHmacOpen] = useState(false)

  function toggleEvent(ev: string) {
    setSelectedEvents(prev =>
      prev.includes(ev) ? prev.filter(e => e !== ev) : [...prev, ev]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    const trimmedUrl = url.trim()
    if (!trimmedUrl) {
      setUrlError(t('createWebhookModal.urlRequired'))
      return
    }
    if (!trimmedUrl.startsWith('https://')) {
      setUrlError(t('createWebhookModal.urlHttps'))
      return
    }
    setUrlError(null)

    setIsLoading(true)
    try {
      const params: CreateWebhookParams = { url: trimmedUrl }
      if (selectedEvents.length > 0) params.event_filter = selectedEvents
      if (secret.trim()) params.secret = secret.trim()

      const resultSecret = await onCreateWebhook(params)
      setCreatedSecret(resultSecret)
      setStep('reveal')
    } catch {
      setFormError(t('createWebhookModal.createError'))
    } finally {
      setIsLoading(false)
    }
  }

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget && step === 'form') {
      onClose()
    }
  }

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={step === 'form' ? t('createWebhookModal.modalAriaLabel') : t('createWebhookModal.revealAriaLabel')}
      data-testid="create-webhook-modal"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="card modal-card"
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: 24,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          boxSizing: 'border-box',
        }}
        data-testid="modal-card"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 12 }}>
          <h2 style={{ margin: 0, flex: 1, fontSize: 18, fontWeight: 600 }}>
            {step === 'form' ? t('createWebhookModal.titleForm') : t('createWebhookModal.titleReveal')}
          </h2>
          {step === 'form' && (
            <button
              className="btn btn-icon btn-ghost"
              onClick={onClose}
              aria-label={t('createWebhookModal.closeBtn')}
              data-testid="modal-x-btn"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Step 1: Formular */}
        {step === 'form' && (
          <form onSubmit={(e) => { void handleSubmit(e) }} noValidate>
            {/* URL-Eingabe */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="webhook-url-input" style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                {t('createWebhookModal.urlLabel')}
              </label>
              <input
                id="webhook-url-input"
                className="input"
                type="url"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder={t('createWebhookModal.urlPlaceholder')}
                required
                aria-required="true"
                aria-invalid={!!urlError}
                aria-describedby={urlError ? 'webhook-url-error' : undefined}
                data-testid="webhook-url-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={isLoading}
              />
              {urlError && (
                <div
                  id="webhook-url-error"
                  role="alert"
                  style={{ color: 'var(--danger, #dc2626)', fontSize: 12, marginTop: 4 }}
                  data-testid="webhook-url-error"
                >
                  {urlError}
                </div>
              )}
            </div>

            {/* Event-Filter-Checkboxen */}
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 8px', fontWeight: 500, fontSize: 14 }}>
                {t('createWebhookModal.eventFilterLabel')}{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-subtle)', fontSize: 12 }}>
                  {t('createWebhookModal.eventFilterHint')}
                </span>
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {WEBHOOK_EVENTS.map(ev => (
                  <label
                    key={ev}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}
                    data-testid={`event-checkbox-label-${ev}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(ev)}
                      onChange={() => toggleEvent(ev)}
                      data-testid={`event-checkbox-${ev}`}
                      disabled={isLoading}
                      style={{ minWidth: 16, minHeight: 16 }}
                    />
                    {EVENT_LABEL_KEYS[ev] ? t(EVENT_LABEL_KEYS[ev]) : ev}
                  </label>
                ))}
              </div>
            </div>

            {/* Optionales Secret */}
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="webhook-secret-input" style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                {t('createWebhookModal.secretLabel')}{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-subtle)', fontSize: 12 }}>
                  {t('createWebhookModal.secretHint')}
                </span>
              </label>
              <input
                id="webhook-secret-input"
                className="input"
                type="text"
                value={secret}
                onChange={e => setSecret(e.target.value)}
                placeholder={t('createWebhookModal.secretPlaceholder')}
                data-testid="webhook-secret-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={isLoading}
              />
            </div>

            {formError && (
              <div role="alert" style={{ color: 'var(--danger, #dc2626)', fontSize: 13, marginBottom: 16 }} data-testid="form-error">
                {formError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={isLoading}
                data-testid="modal-cancel-btn"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
                aria-busy={isLoading}
                data-testid="modal-submit-btn"
              >
                {isLoading ? t('createWebhookModal.submitCreating') : t('createWebhookModal.submitCreate')}
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Secret-Einmal-Anzeige */}
        {step === 'reveal' && createdSecret && (
          <div>
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                background: 'color-mix(in srgb, #f59e0b 12%, transparent)',
                border: '1px solid color-mix(in srgb, #f59e0b 40%, transparent)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--text-h)',
                marginBottom: 16,
              }}
              data-testid="secret-warning"
            >
              {t('createWebhookModal.secretWarning')}
            </div>

            <TokenRevealBox token={createdSecret} />

            {/* HMAC-Hinweis */}
            <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setHmacOpen(v => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: 'var(--surface, color-mix(in srgb, var(--text) 4%, transparent))',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--text-h)',
                  textAlign: 'left',
                }}
                aria-expanded={hmacOpen}
                data-testid="hmac-toggle"
              >
                {t('createWebhookModal.hmacToggle')}
                {hmacOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {hmacOpen && (
                <pre
                  data-testid="hmac-code"
                  style={{
                    margin: 0,
                    padding: '12px 14px',
                    overflowX: 'auto',
                    fontSize: 12,
                    fontFamily: 'ui-monospace, Consolas, monospace',
                    background: 'color-mix(in srgb, var(--text) 4%, transparent)',
                    color: 'var(--text-h)',
                    borderTop: '1px solid var(--border)',
                  }}
                >
{`${t('webhooks.codeExample.comment')}
const crypto = require('crypto')
const signature = request.headers['x-heliumtracker-signature']
const expected = 'sha256=' + crypto
  .createHmac('sha256', WEBHOOK_SECRET)
  .update(request.body)
  .digest('hex')
if (!crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected))) {
  throw new Error('${t('webhooks.codeExample.invalidSignature')}')
}`}
                </pre>
              )}
            </div>

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary"
                onClick={onClose}
                data-testid="modal-close-btn"
              >
                {t('common:actions.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CreateWebhookModal({ isOpen, onClose, onCreateWebhook }: CreateWebhookModalProps) {
  if (!isOpen) return null
  return (
    <CreateWebhookModalContent onClose={onClose} onCreateWebhook={onCreateWebhook} />
  )
}

// --- Main Page ---

type WebhooksPageProps = {
  /**
   * Story 5.2: 'embedded' rendert die Webhook-Verwaltung als Abschnitt im
   * konsolidierten Developer-Portal — h2 statt h1, div statt main.
   */
  variant?: 'page' | 'embedded'
}

export function WebhooksPage({ variant = 'page' }: WebhooksPageProps = {}) {
  const embedded = variant === 'embedded'
  const Root = embedded ? 'div' : 'main'
  const Heading = embedded ? 'h2' : 'h1'
  const {
    webhooks,
    isLoading,
    isError,
    isPremiumError,
    refetch,
    createWebhook,
    deleteWebhook,
    testWebhook,
    deletingId,
    testingId,
  } = useWebhooks()
  // Root-Cause-Fix (heliumtracker-web#21): Der Backend-Guard von
  // /api/v2/webhooks prueft nur current_user.premium?, waehrend
  // /api/v2/api_tokens zusaetzlich den Kontingent-Zugang (quota_limit > 0)
  // verlangt — Tarife ohne API-Zugang sahen Webhooks daher "offen", API-Tokens
  // aber "gesperrt". Bis der Backend-Guard angeglichen ist, erzwingt das
  // Frontend hier dieselbe Bedingung ueber die gemeinsame api_access-Quelle.
  // Fail-closed (wie ApiKeyCard.tsx: `usage?.api_access ?? false`): schlaegt
  // der Usage-Fetch fehl (isUsageLoading:false, usage:null), gilt der Zugang
  // als GESPERRT statt die volle Verwaltungs-UI durchzureichen.
  const { usage, isLoading: isUsageLoading } = useApiUsage()
  const accessDenied = isPremiumError || (!isUsageLoading && !(usage?.api_access ?? false))
  const { t } = useTranslation('developer')

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<WebhookConfig | null>(null)

  async function handleCreateWebhook(params: CreateWebhookParams): Promise<string> {
    const result = await createWebhook(params)
    return result.secret
  }

  function handleDeleteClick(id: number) {
    const webhook = webhooks.find(w => w.id === id)
    if (webhook) setDeleteTarget(webhook)
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    try {
      await deleteWebhook(deleteTarget.id)
    } finally {
      setDeleteTarget(null)
    }
  }

  // Race-Fix: Webhooks- und Usage-Query laden unabhaengig. Solange die
  // Usage-Antwort (und damit accessDenied) noch aussteht, darf die volle
  // Verwaltungs-UI nicht kurz aufblitzen, nur weil die Webhooks-Liste
  // schneller zurueckkommt — erst rendern, wenn der Zugangsstatus feststeht.
  if (isUsageLoading) {
    return (
      <Root className="webhooks-page">
        <LoadingSkeleton />
      </Root>
    )
  }

  if (accessDenied) {
    return <PremiumRequired />
  }

  return (
    <Root className="webhooks-page">
      <header className="page-header" style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Heading style={{ margin: 0 }}>{t('webhooks.pageTitle')}</Heading>
          {embedded && (
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-subtle)' }}>
              {t('webhooks.subtitle')}
            </p>
          )}
        </div>
        <DemoGuardedButton
          className="btn btn-primary"
          onClick={() => setIsCreateModalOpen(true)}
          data-testid="btn-create-webhook"
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
        >
          <Plus size={16} aria-hidden="true" />
          {t('webhooks.createBtn')}
        </DemoGuardedButton>
      </header>

      <section aria-label={t('webhooks.listLabel')}>
        {isLoading && <LoadingSkeleton />}

        {isError && !isLoading && <ErrorState onRetry={refetch} />}

        {!isLoading && !isError && webhooks.length === 0 && (
          <EmptyState onCreateClick={() => setIsCreateModalOpen(true)} />
        )}

        {!isLoading && !isError && webhooks.length > 0 && (
          <div
            role="list"
            aria-label={t('webhooks.listAriaLabel')}
            data-testid="webhooks-list"
            style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}
          >
            {webhooks.map(webhook => (
              <WebhookRow
                key={webhook.id}
                webhook={webhook}
                onDelete={handleDeleteClick}
                onTest={async (id) => testWebhook(id)}
                isDeleting={deletingId === webhook.id}
                isTesting={testingId === webhook.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* Create-Modal */}
      <CreateWebhookModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWebhook={handleCreateWebhook}
      />

      {/* Delete-Confirm */}
      {deleteTarget && (
        <DeleteConfirmDialog
          webhookUrl={deleteTarget.url}
          onConfirm={() => { void handleDeleteConfirm() }}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={deletingId === deleteTarget.id}
        />
      )}
    </Root>
  )
}
